import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Syringe, PawPrint, Bug, Pencil, X } from "lucide-react";
import "../styles/PetfamCalendar.css";
import { authFetch } from "../utils/authFetch";

import config from "../config";

const API_BASE_URL = config.apiBaseUrl;

const WEEK_DAYS = ["L", "M", "M", "J", "V", "S", "D"];

// Fechas especiales fijas (mes en índice JS: 0 = enero)
const PET_EVENTS = [
  {
    id: "dia-mascotas",
    month: 3,
    day: 11,
    title: "Día Internacional de las Mascotas",
    tag: "Fecha especial",
    description:
      "Un día para celebrar a todas las mascotas con amor, juegos y muchos mimos.",
    type: "special",
  },
  {
    id: "dia-animales",
    month: 9,
    day: 4,
    title: "Día Mundial de los Animales",
    tag: "Fecha especial",
    description:
      "Recordamos la importancia del bienestar y protección de todos los animales del mundo.",
    type: "special",
  },
];

function getCalendarMatrix(currentMonth) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstOfMonth = new Date(year, month, 1);

  let startDay = firstOfMonth.getDay();
  let diffToMonday = (startDay + 6) % 7;
  const calendarStart = new Date(year, month, 1 - diffToMonday);

  const matrix = [];
  let current = new Date(calendarStart);

  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    matrix.push(row);
  }

  return { matrix };
}

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateKey(dateKey) {
  if (!dateKey || typeof dateKey !== "string") return null;

  const base = String(dateKey).slice(0, 10);
  const [y, m, d] = base.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function normalizeYYYYMMDD(x) {
  if (!x) return null;
  const s = String(x).trim();
  if (!s) return null;
  const base = s.length >= 10 ? s.slice(0, 10) : s;
  return /^\d{4}-\d{2}-\d{2}$/.test(base) ? base : null;
}

function getEventsForDate(
  date,
  birthdayEvents = [],
  vaccineEvents = [],
  dewormingEvents = []
) {
  const m = date.getMonth();
  const d = date.getDate();
  const key = toDateKey(date);

  const specials = PET_EVENTS.filter((ev) => ev.month === m && ev.day === d);
  const birthdays = birthdayEvents.filter((ev) => ev.month === m && ev.day === d);
  const vaccines = vaccineEvents.filter((ev) => ev.dateKey === key);
  const dewormings = dewormingEvents.filter((ev) => ev.dateKey === key);

  const specialsNorm = specials.map((ev) => ({ ...ev, type: "special" }));
  const birthdaysNorm = birthdays.map((ev) => ({ ...ev, type: "birthday" }));
  const vaccinesNorm = vaccines.map((ev) => ({ ...ev, type: "vaccine" }));
  const dewormingsNorm = dewormings.map((ev) => ({ ...ev, type: "deworming" }));

  return [...vaccinesNorm, ...dewormingsNorm, ...specialsNorm, ...birthdaysNorm];
}

const normStatus = (s) => String(s || "").trim().toUpperCase();
const isDoneStatus = (s) => normStatus(s) === "HECHO" || normStatus(s) === "DONE";

function CalendarDateField({ value, onChange }) {
  const inputRef = React.useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;

    try {
      if (typeof input.showPicker === "function") input.showPicker();
      else {
        input.focus();
        input.click();
      }
    } catch {
      input.focus();
      input.click();
    }
  };

  const formatDate = (v) => {
    if (!v) return "Seleccionar fecha";
    const [y, m, d] = String(v).split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="pf-edit-date-field" onClick={openPicker}>
      <span>{formatDate(value)}</span>

      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Seleccionar fecha"
      />
    </div>
  );
}

export default function PetfamCalendar({
  onDateSelect,
  birthdayEvents = [],
  idUser,
  pets = [],
  refreshKey = 0,
  onScheduleConsult,
  onOpenDrPaws,
  activePetId,
  planActive,
}) {
  const navigate = useNavigate();

  const [planModal, setPlanModal] = useState({ open: false });

  const [editDateModal, setEditDateModal] = useState({
  open: false,
  event: null,
  date: "",
  saving: false,
  error: "",
});

  const requirePlan = () => {
    setPlanModal({ open: true });
  };

  const goToPlan = () => {
    setPlanModal({ open: false });
    navigate("/tu-plan-2", { state: { from: "calendar" } });
  };

  const isBlocked = planActive === false;

  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const [flashDateKey, setFlashDateKey] = useState(null);

  const [vaccineEvents, setVaccineEvents] = useState([]);
  const [dewormingEvents, setDewormingEvents] = useState([]);
  const [loadingVaccines, setLoadingVaccines] = useState(false);

  const [openCategory, setOpenCategory] = useState(null);

  const { matrix } = useMemo(() => getCalendarMatrix(viewDate), [viewDate]);

  const resolvedUserId = useMemo(() => {
    if (idUser != null && idUser !== "") return Number(idUser);
    const raw = localStorage.getItem("idUser");
    return raw ? Number(raw) : null;
  }, [idUser]);

  const petsMap = useMemo(() => {
    const map = {};
    (pets || []).forEach((p) => {
      const id = p.idPet ?? p.id_pet ?? p.id;
      const name = p.name ?? p.nombre ?? p.petName;
      if (id != null && name) map[String(id)] = name;
    });
    return map;
  }, [pets]);

  const mapEvents = (data = []) => {
    const mapped = (Array.isArray(data) ? data : [])
      .map((ev) => {
        const dateKeyRaw =
          ev.eventDate ||
          ev.event_date ||
          ev.event_date_time ||
          ev.eventDateTime ||
          ev.date ||
          ev.dateKey ||
          null;

        const dateKey = normalizeYYYYMMDD(dateKeyRaw);

        const idPet = ev.idPet ?? ev.id_pet ?? ev.petId ?? null;
        const idEvent = ev.idEvent ?? ev.id_event ?? ev.id ?? null;

        const eventTypeRaw = ev.eventType ?? ev.event_type ?? ev.type ?? ev.tag ?? "";
        const eventType = String(eventTypeRaw || "").trim().toUpperCase();

        const title = ev.title ?? ev.vaccineName ?? ev.name ?? "Evento";
        const description = ev.description ?? ev.details ?? "";

        return {
          id: `cal-${idEvent ?? Math.random()}`,
          idEvent,
          dateKey,
          idPet,
          title,
          description,
          status: ev.status,
          eventType,
          raw: ev,
        };
      })
      .filter((x) => !!x.dateKey);

    return mapped;
  };

  useEffect(() => {
    if (!resolvedUserId) return;

    const loadEvents = async () => {
      try {
        setLoadingVaccines(true);

        const res = await authFetch(`/calendar/events?idUser=${resolvedUserId}`, {
          method: "GET",
        });
        const json = await res.json().catch(() => null);

        if (!res.ok) throw new Error("No se pudieron cargar eventos");

        const data = Array.isArray(json?.data) ? json.data : [];
        const events = mapEvents(data);

        const todayDate = new Date();
const currentMonthStart = new Date(
  todayDate.getFullYear(),
  todayDate.getMonth(),
  1
);

const visibleEvents = events.filter((ev) => {
  const done = isDoneStatus(ev.status);

  if (!done) return true;

  const eventDate = parseDateKey(ev.dateKey);
  if (!eventDate) return true;

  const eventMonthStart = new Date(
    eventDate.getFullYear(),
    eventDate.getMonth(),
    1
  );

  return eventMonthStart >= currentMonthStart;
});

        const vaccines = visibleEvents.filter((x) => {
          const t = String(x.eventType || "").toUpperCase();
          const title = String(x.title || "").toUpperCase();
          return t === "VACCINE" || t === "VACUNA" || title.includes("VACUNA");
        });

        const dewormings = visibleEvents.filter((x) => {
          const t = String(x.eventType || "").toUpperCase();
          return t === "DEWORMING_INTERNAL" || t === "DEWORMING_EXTERNAL";
        });

        setVaccineEvents(vaccines);
        setDewormingEvents(dewormings);
      } catch (e) {
        console.error(e);
        setVaccineEvents([]);
        setDewormingEvents([]);
      } finally {
        setLoadingVaccines(false);
      }
    };

    loadEvents();
  }, [resolvedUserId, refreshKey]);

  const vaccineKeysSet = useMemo(() => {
    const set = new Set();
    (vaccineEvents || []).forEach((v) => {
      if (v?.dateKey) set.add(v.dateKey);
    });
    return set;
  }, [vaccineEvents]);

  const dewormingKeysSet = useMemo(() => {
    const set = new Set();
    (dewormingEvents || []).forEach((v) => {
      if (v?.dateKey) set.add(v.dateKey);
    });
    return set;
  }, [dewormingEvents]);

  const pendingVaccineKeysSet = useMemo(() => {
    const set = new Set();
    (vaccineEvents || []).forEach((v) => {
      if (v?.dateKey && !isDoneStatus(v.status)) set.add(v.dateKey);
    });
    return set;
  }, [vaccineEvents]);

  const pendingDewormingKeysSet = useMemo(() => {
    const set = new Set();
    (dewormingEvents || []).forEach((v) => {
      if (v?.dateKey && !isDoneStatus(v.status)) set.add(v.dateKey);
    });
    return set;
  }, [dewormingEvents]);

  const selectedEvents = useMemo(() => {
    return selectedDate
      ? getEventsForDate(selectedDate, birthdayEvents, vaccineEvents, dewormingEvents)
      : [];
  }, [selectedDate, birthdayEvents, vaccineEvents, dewormingEvents]);

  const allVaccines = useMemo(() => {
    return (vaccineEvents || [])
      .map((ev) => {
        const d = parseDateKey(ev.dateKey);
        return d ? { ...ev, type: "vaccine", _dateObj: d } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._dateObj - b._dateObj);
  }, [vaccineEvents]);

  const allDewormings = useMemo(() => {
    return (dewormingEvents || [])
      .map((ev) => {
        const d = parseDateKey(ev.dateKey);
        return d ? { ...ev, type: "deworming", _dateObj: d } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._dateObj - b._dateObj);
  }, [dewormingEvents]);

  const petsWithoutPlan = useMemo(() => {
    const petsArr = Array.isArray(pets) ? pets : [];
    if (!petsArr.length) return [];

    const petIdSetWithVaccines = new Set(
      (vaccineEvents || [])
        .map((v) => v?.idPet)
        .filter((x) => x != null)
        .map((x) => String(x))
    );

    return petsArr
      .map((p) => {
        const id = p.idPet ?? p.id_pet ?? p.id ?? null;
        const name = p.name ?? p.nombre ?? p.petName ?? null;
        return { id, name, raw: p };
      })
      .filter((p) => p.id != null)
      .filter((p) => !petIdSetWithVaccines.has(String(p.id)));
  }, [pets, vaccineEvents]);

  const petsWithoutDewormingPlan = useMemo(() => {
    const petsArr = Array.isArray(pets) ? pets : [];
    if (!petsArr.length) return [];

    const petIdSetWithDeworming = new Set(
      (dewormingEvents || [])
        .map((v) => v?.idPet)
        .filter((x) => x != null)
        .map((x) => String(x))
    );

    return petsArr
      .map((p) => {
        const id = p.idPet ?? p.id_pet ?? p.id ?? null;
        const name = p.name ?? p.nombre ?? p.petName ?? null;
        return { id, name, raw: p };
      })
      .filter((p) => p.id != null)
      .filter((p) => !petIdSetWithDeworming.has(String(p.id)));
  }, [pets, dewormingEvents]);

  const allSpecials = useMemo(() => {
    const y = viewDate.getFullYear();
    return PET_EVENTS.map((ev) => {
      const d = new Date(y, ev.month, ev.day);
      return { ...ev, type: "special", _dateObj: d };
    }).sort((a, b) => a._dateObj - b._dateObj);
  }, [viewDate]);

  const allBirthdays = useMemo(() => {
    const y = viewDate.getFullYear();
    const now = new Date();
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return (birthdayEvents || [])
      .map((ev) => {
        if (ev?.month == null || ev?.day == null) return null;
        let d = new Date(y, ev.month, ev.day);
        if (d < todayOnly) d = new Date(y + 1, ev.month, ev.day);
        return { ...ev, type: "birthday", _dateObj: d };
      })
      .filter(Boolean)
      .sort((a, b) => a._dateObj - b._dateObj);
  }, [birthdayEvents, viewDate]);

  const categoryList = useMemo(() => {
    if (openCategory === "vaccine") return allVaccines;
    if (openCategory === "deworming") return allDewormings;
    if (openCategory === "birthday") return allBirthdays;
    if (openCategory === "special") return allSpecials;
    return [];
  }, [openCategory, allVaccines, allDewormings, allBirthdays, allSpecials]);

  const monthLabel = useMemo(() => {
    const monthName = viewDate
      .toLocaleDateString("es-ES", { month: "long" })
      .replace(/^./, (c) => c.toUpperCase());
    const year = viewDate.getFullYear();
    return `${monthName} ${year}`;
  }, [viewDate]);

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isInCurrentMonth = (date) =>
    date.getMonth() === viewDate.getMonth() &&
    date.getFullYear() === viewDate.getFullYear();

  const goPrevMonth = () => {
    if (isBlocked) return requirePlan();
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedDate(null);
    setOpenCategory(null);
  };

  const goNextMonth = () => {
    if (isBlocked) return requirePlan();
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedDate(null);
    setOpenCategory(null);
  };

  const handleSelect = (date) => {
    if (isBlocked) return requirePlan();

    setOpenCategory(null);

    if (selectedDate && isSameDay(selectedDate, date)) {
      setSelectedDate(null);
      onDateSelect?.(null);
    } else {
      setSelectedDate(date);
      onDateSelect?.(date);
    }
  };

  const toggleCategory = (cat) => {
    if (isBlocked) return requirePlan();

    setSelectedDate(null);
    onDateSelect?.(null);
    setOpenCategory((prev) => (prev === cat ? null : cat));
  };

  const jumpToEvent = (ev) => {
    if (isBlocked) return requirePlan();

    let dateObj = ev?._dateObj || null;
    if (!dateObj && (ev?.type === "vaccine" || ev?.type === "deworming")) {
      dateObj = parseDateKey(ev.dateKey);
    }
    if (!dateObj) return;

    const target = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);

    setViewDate(target);
    setSelectedDate(dateObj);
    setOpenCategory(null);
    onDateSelect?.(dateObj);

    const k = toDateKey(dateObj);
    setFlashDateKey(k);
    window.setTimeout(() => setFlashDateKey(null), 1100);
  };

  const formatListDate = (ev) => {
    const d = ev?._dateObj || (ev?.dateKey ? parseDateKey(ev.dateKey) : null);
    return d
      ? d.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "";
  };

  const shouldShowConsultBtn = (ev) => {
    if (!ev || ev.type !== "vaccine") return false;
    const t = String(ev.title || "").toLowerCase();
    const d = String(ev.description || "").toLowerCase();
    return (
      t.includes("revisión") ||
      t.includes("revision") ||
      t.includes("esquema") ||
      d.includes("veterinario") ||
      d.includes("consulta")
    );
  };

  const handleScheduleConsult = (ev) => {
    if (isBlocked) return requirePlan();

    const petId =
      ev?.idPet ??
      ev?.raw?.idPet ??
      ev?.raw?.id_pet ??
      ev?.raw?.petId ??
      null;
    if (!petId) return;

    if (typeof onScheduleConsult === "function") {
      onScheduleConsult(petId, ev);
      return;
    }

    navigate(`/informascota?petId=${petId}`, { state: { petId } });
  };

  const normalizeDescription = (desc) => {
    if (!desc) return desc;
    return String(desc).replace(/el veterinario/gi, "nuestro veterinario");
  };

  const handleOpenDrPaws = (petIdOverride) => {
    if (isBlocked) return requirePlan();

    const petId =
      petIdOverride ??
      activePetId ??
      (pets?.[0]?.idPet ?? pets?.[0]?.id ?? null);

    if (!petId) return;

    if (typeof onOpenDrPaws === "function") {
      onOpenDrPaws(petId);
      return;
    }

    console.warn("onOpenDrPaws no fue provisto");
  };

  const reloadEvents = async () => {
    if (!resolvedUserId) return;

    const r2 = await authFetch(`/calendar/events?idUser=${resolvedUserId}`, {
      method: "GET",
    });
    const j2 = await r2.json().catch(() => null);
    const data2 = Array.isArray(j2?.data) ? j2.data : [];
    const events = mapEvents(data2);

    const todayDate = new Date();
const currentMonthStart = new Date(
  todayDate.getFullYear(),
  todayDate.getMonth(),
  1
);

const visibleEvents = events.filter((ev) => {
  const done = isDoneStatus(ev.status);

  if (!done) return true;

  const eventDate = parseDateKey(ev.dateKey);
  if (!eventDate) return true;

  const eventMonthStart = new Date(
    eventDate.getFullYear(),
    eventDate.getMonth(),
    1
  );

  return eventMonthStart >= currentMonthStart;
});

    const vaccines = visibleEvents.filter((x) => {
      const t = String(x.eventType || "").toUpperCase();
      const title = String(x.title || "").toUpperCase();
      return t === "VACCINE" || t === "VACUNA" || title.includes("VACUNA");
    });

    const dewormings = visibleEvents.filter((x) => {
      const t = String(x.eventType || "").toUpperCase();
      return t === "DEWORMING_INTERNAL" || t === "DEWORMING_EXTERNAL";
    });

    setVaccineEvents(vaccines);
    setDewormingEvents(dewormings);
  };

  const markVaccineDone = async (ev) => {
    if (isBlocked) return requirePlan();

    const idEvent =
      ev?.idEvent ??
      ev?.raw?.idEvent ??
      ev?.raw?.id_event ??
      ev?.raw?.id ??
      null;

    if (!idEvent) return;

    setVaccineEvents((prev) =>
      prev.map((x) => {
        const xId =
          x?.idEvent ?? x?.raw?.idEvent ?? x?.raw?.id_event ?? x?.raw?.id ?? null;
        return xId === idEvent ? { ...x, status: "HECHO" } : x;
      })
    );

    try {
      const res = await authFetch(`/calendar/events/${idEvent}/done`, {
        method: "PUT",
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        console.error("done endpoint error:", res.status, json);
        throw new Error(json?.userMessage || `Backend respondió ${res.status}`);
      }

      await reloadEvents();
    } catch (err) {
      console.error("Error marcando vacuna como HECHO:", err);
      try {
        await reloadEvents();
      } catch {}
    }
  };

 const editVaccineDate = (ev) => {
  if (isBlocked) return requirePlan();

  setEditDateModal({
    open: true,
    event: ev,
    date: ev?.dateKey || "",
    saving: false,
    error: "",
  });
};

const closeEditDateModal = () => {
  setEditDateModal({
    open: false,
    event: null,
    date: "",
    saving: false,
    error: "",
  });
};

const saveEditEventDate = async () => {
  const ev = editDateModal.event;

  const idEvent =
    ev?.idEvent ??
    ev?.raw?.idEvent ??
    ev?.raw?.id_event ??
    ev?.raw?.id ??
    null;

  if (!idEvent) return;

  if (!editDateModal.date) {
    setEditDateModal((prev) => ({ ...prev, error: "Selecciona una fecha." }));
    return;
  }

  try {
    setEditDateModal((prev) => ({ ...prev, saving: true, error: "" }));

    const res = await authFetch(
      `/calendar/events/${idEvent}/reschedule?newDate=${editDateModal.date}`,
      { method: "PUT" }
    );

    const json = await res.json().catch(() => null);

    if (!res.ok) {
      throw new Error(json?.userMessage || "No se pudo cambiar la fecha.");
    }

    await reloadEvents();
    closeEditDateModal();
  } catch (err) {
    setEditDateModal((prev) => ({
      ...prev,
      saving: false,
      error: err.message || "No se pudo cambiar la fecha.",
    }));
  }
};

  const markDewormingDone = async (ev) => {
    if (isBlocked) return requirePlan();

    const idEvent =
      ev?.idEvent ??
      ev?.raw?.idEvent ??
      ev?.raw?.id_event ??
      ev?.raw?.id ??
      null;

    if (!idEvent) return;

    setDewormingEvents((prev) =>
      prev.map((x) => {
        const xId =
          x?.idEvent ?? x?.raw?.idEvent ?? x?.raw?.id_event ?? x?.raw?.id ?? null;
        return xId === idEvent ? { ...x, status: "HECHO" } : x;
      })
    );

    try {
      const res = await authFetch(`/calendar/events/${idEvent}/done`, {
        method: "PUT",
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        console.error("done endpoint error:", res.status, json);
        throw new Error(json?.userMessage || `Backend respondió ${res.status}`);
      }

      await reloadEvents();
    } catch (err) {
      console.error("Error marcando desparasitación como HECHO:", err);
      try {
        await reloadEvents();
      } catch {}
    }
  };

  const MissingPlanCard = ({ petId, petName }) => (
    <article className="pf-cal-event-card">
      <h5 className="pf-cal-event-title">{petName} no tiene plan de vacunación</h5>

      <p className="pf-cal-event-desc">
        Cuando Dra.Vetia genere el plan para {petName}, aquí aparecerán todas sus vacunas.
      </p>

      <button
        type="button"
        className="pf-cal-drpaws-btn"
        onClick={() => handleOpenDrPaws(petId)}
      >
        <PawPrint size={16} />
        Dra.Vetia
      </button>
    </article>
  );

  const MissingDewormingPlanCard = ({ petId, petName }) => (
    <article className="pf-cal-event-card">
      <h5 className="pf-cal-event-title">{petName} no tiene plan de desparasitación</h5>

      <p className="pf-cal-event-desc">
        Cuando Dra.Vetia genere el plan para {petName}, aquí aparecerán todas sus desparasitaciones.
      </p>

      <button
        type="button"
        className="pf-cal-drpaws-btn"
        onClick={() => handleOpenDrPaws(petId)}
      >
        <PawPrint size={16} />
        Dra.Vetia
      </button>
    </article>
  );

  return (
    <div className="pf-calendar">
      {planModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 9999,
          }}
          onClick={() => setPlanModal({ open: false })}
        >
          <div
            style={{
              width: "min(420px, 92vw)",
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 18px 50px rgba(0,0,0,.20)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 900, color: "#003642", marginBottom: 8 }}>
              Tu plan está inactivo
            </div>
            <div style={{ fontSize: 14, color: "#355a63", lineHeight: 1.4 }}>
              Para usar el calendario y los servicios, necesitas reactivar tu suscripción.
            </div>

            <button
              style={{
                marginTop: 14,
                width: "100%",
                padding: "11px 14px",
                borderRadius: 12,
                border: "none",
                background: "#0095A9",
                color: "#fff",
                fontWeight: 800,
                cursor: "pointer",
              }}
              onClick={goToPlan}
            >
              Ir a tu plan
            </button>

            <button
              style={{
                marginTop: 10,
                width: "100%",
                padding: "11px 14px",
                borderRadius: 12,
                border: "1px solid #dcdcdc",
                background: "#fff",
                color: "#003642",
                fontWeight: 800,
                cursor: "pointer",
              }}
              onClick={() => setPlanModal({ open: false })}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {editDateModal.open &&
  createPortal(
    <div className="pf-edit-date-overlay" onClick={closeEditDateModal}>
      <div className="pf-edit-date-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="pf-edit-date-close"
          onClick={closeEditDateModal}
        >
          <X size={18} />
        </button>

        <div className="pf-edit-date-icon">
          <Pencil size={22} />
        </div>

        <h3>Cambiar fecha</h3>

        <p>
          Selecciona la nueva fecha para este evento.
        </p>

        <CalendarDateField
  value={editDateModal.date}
  onChange={(value) =>
    setEditDateModal((prev) => ({
      ...prev,
      date: value,
      error: "",
    }))
  }
/>

        {editDateModal.error && (
          <span className="pf-edit-date-error">{editDateModal.error}</span>
        )}

        <div className="pf-edit-date-actions">
          <button type="button" onClick={closeEditDateModal}>
            Cancelar
          </button>

          <button
            type="button"
            onClick={saveEditEventDate}
            disabled={editDateModal.saving}
          >
            {editDateModal.saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

      <header className="pf-cal-header">
        <button
          type="button"
          className="pf-cal-nav-btn"
          onClick={goPrevMonth}
          aria-label="Mes anterior"
        >
          ‹
        </button>
        <h3 className="pf-cal-month">{monthLabel}</h3>
        <button
          type="button"
          className="pf-cal-nav-btn"
          onClick={goNextMonth}
          aria-label="Mes siguiente"
        >
          ›
        </button>
      </header>

      <div className="pf-cal-weekdays">
        {WEEK_DAYS.map((d, idx) => (
          <span key={`${d}-${idx}`}>{d}</span>
        ))}
      </div>

      <div className="pf-cal-grid">
        {matrix.flat().map((date) => {
          const key = date.toISOString();
          const inMonth = isInCurrentMonth(date);
          const isToday = isSameDay(date, today);
          const isSelected = isSameDay(date, selectedDate);

          const kDate = toDateKey(date);
          const hasVaccine = vaccineKeysSet.has(kDate);
          const hasDeworming = dewormingKeysSet.has(kDate);
          const hasPendingVaccine = pendingVaccineKeysSet.has(kDate);
          const hasOnlyDoneVaccines = hasVaccine && !hasPendingVaccine;
          const hasPendingDeworming = pendingDewormingKeysSet.has(kDate);

          const dayEvents = getEventsForDate(date, birthdayEvents, vaccineEvents, dewormingEvents);
          const hasSpecial = dayEvents.some((e) => e.type === "special");
          const hasBirthday = dayEvents.some((e) => e.type === "birthday");

          const isFlashing = flashDateKey && flashDateKey === kDate;

          let classes = "pf-cal-day";
          if (!inMonth) classes += " pf-cal-day--muted";
          if (hasVaccine && hasPendingVaccine) classes += " pf-cal-day--vaccine";
          if (hasOnlyDoneVaccines) classes += " pf-cal-day--vaccine-done";
          if (hasDeworming) classes += hasPendingDeworming
            ? " pf-cal-day--deworming"
            : " pf-cal-day--deworming-done";
          if (hasSpecial) classes += " pf-cal-day--event";
          if (hasBirthday) classes += " pf-cal-day--birthday";
          if (isToday) classes += " pf-cal-day--today";
          if (isSelected) classes += " pf-cal-day--selected";
          if (isFlashing) classes += " pf-cal-day--flash";
          if (isBlocked) classes += " pf-cal-day--disabled";

          return (
            <button
              type="button"
              key={key}
              className={classes}
              onClick={() => handleSelect(date)}
              title={isBlocked ? "Plan inactivo" : undefined}
            >
              <span>{date.getDate()}</span>

              {hasVaccine && (
                <span className="pf-cal-vaccine-icon" aria-hidden="true">
                  <Syringe size={12} strokeWidth={2.2} />
                </span>
              )}

              {hasDeworming && (
                <span className="pf-cal-deworming-icon" aria-hidden="true">
                  <Bug size={12} strokeWidth={2.2} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="pf-cal-schedule">
        <div className="pf-cal-schedule-header">
          <button
            type="button"
            className={`pf-cal-legend pf-cal-legend--vaccine ${
              openCategory === "vaccine" ? "is-open" : ""
            }`}
            onClick={() => toggleCategory("vaccine")}
            aria-pressed={openCategory === "vaccine"}
          >
            <span className="pf-cal-legend-icon" aria-hidden="true">
              <Syringe size={14} strokeWidth={2.2} />
            </span>
            <span className="pf-cal-legend-label">
              Vacunas{loadingVaccines ? "…" : ""}
            </span>
          </button>

          <button
            type="button"
            className={`pf-cal-legend pf-cal-legend--deworming ${
              openCategory === "deworming" ? "is-open" : ""
            }`}
            onClick={() => toggleCategory("deworming")}
            aria-pressed={openCategory === "deworming"}
          >
            <span className="pf-cal-legend-icon pf-cal-legend-icon--deworming" aria-hidden="true">
              <Bug size={14} strokeWidth={2.2} />
            </span>
            <span className="pf-cal-legend-label">Desparasitaciones</span>
          </button>

          <button
            type="button"
            className={`pf-cal-legend pf-cal-legend--birthday ${
              openCategory === "birthday" ? "is-open" : ""
            }`}
            onClick={() => toggleCategory("birthday")}
            aria-pressed={openCategory === "birthday"}
          >
            <span className="pf-cal-legend-dot" />
            <span className="pf-cal-legend-label">Cumpleaños</span>
          </button>

          <button
            type="button"
            className={`pf-cal-legend pf-cal-legend--special ${
              openCategory === "special" ? "is-open" : ""
            }`}
            onClick={() => toggleCategory("special")}
            aria-pressed={openCategory === "special"}
          >
            <span className="pf-cal-legend-dot" />
            <span className="pf-cal-legend-label">Fechas especiales</span>
          </button>
        </div>

        {openCategory && (
          <div className="pf-cal-events-list">
            {openCategory === "vaccine" ? (
              <>
                {petsWithoutPlan.length > 0 &&
                  petsWithoutPlan.map((p) => (
                    <MissingPlanCard
                      key={`missing-${p.id}`}
                      petId={p.id}
                      petName={p.name || "Tu mascota"}
                    />
                  ))}

                {categoryList.length === 0 ? (
                  petsWithoutPlan.length > 0 ? null : (
                    <article className="pf-cal-event-card">
                      <h5 className="pf-cal-event-title">No tienes vacunas registradas</h5>

                      <p className="pf-cal-event-desc">
                        Cuando Dra.Vetia genere un plan, aquí aparecerán todas las vacunas.
                      </p>

                      <button
                        type="button"
                        className="pf-cal-drpaws-btn"
                        onClick={() => handleOpenDrPaws()}
                      >
                        <PawPrint size={16} />
                        Dra. Vetia
                      </button>
                    </article>
                  )
                ) : (
                  categoryList.map((ev) => {
                    const petName = petsMap[String(ev.idPet)] || "tu mascota";
                    const showConsult = shouldShowConsultBtn(ev);
                    const done = isDoneStatus(ev.status);

                    return (
                      <div
                        key={ev.id}
                        role="button"
                        tabIndex={0}
                        className={`pf-cal-event-card pf-cal-event-card--${ev.type} pf-cal-event-card--clickable ${
                          ev.type === "vaccine" && done ? "is-done" : ""
                        }`}
                        onClick={() => jumpToEvent(ev)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") jumpToEvent(ev);
                        }}
                        title={isBlocked ? "Plan inactivo" : "Ver en el calendario"}
                      >
                        <div className="pf-cal-event-pill">{`Vacuna de ${petName}`}</div>

                        <h5 className="pf-cal-event-title">{ev.title}</h5>
                        <p className="pf-cal-event-date">{formatListDate(ev)}</p>

                        {!done && (
  <button
    type="button"
    className="pf-cal-edit-date-btn"
    onClick={(e) => {
      e.stopPropagation();
      editVaccineDate(ev);
    }}
  >
    <Pencil size={14} />
    Cambiar fecha
  </button>
)}

                        {ev.description && (
                          <p className="pf-cal-event-desc">
                            {normalizeDescription(ev.description)}
                          </p>
                        )}

                        {ev.status && (
                          <p className="pf-cal-event-status">
                            Estado: <strong>{normStatus(ev.status)}</strong>
                          </p>
                        )}

                        {!done && (
                          <button
                            type="button"
                            className="pf-cal-done-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await markVaccineDone(ev);
                            }}
                          >
                            Marcar como aplicada
                          </button>
                        )}

                        {done && (
                          <button
                            type="button"
                            className="pf-cal-done-btn is-done"
                            disabled
                          >
                            Hecho
                          </button>
                        )}

                        {showConsult && (
                          <button
                            type="button"
                            className="pf-cal-consult-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScheduleConsult(ev);
                            }}
                          >
                            Programar consulta
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            ) : openCategory === "deworming" ? (
              <>
                {petsWithoutDewormingPlan.length > 0 &&
                  petsWithoutDewormingPlan.map((p) => (
                    <MissingDewormingPlanCard
                      key={`missing-deworming-${p.id}`}
                      petId={p.id}
                      petName={p.name || "Tu mascota"}
                    />
                  ))}

                {categoryList.length === 0 ? (
                  petsWithoutDewormingPlan.length > 0 ? null : (
                    <article className="pf-cal-event-card">
                      <h5 className="pf-cal-event-title">No tienes desparasitaciones registradas</h5>
                      <p className="pf-cal-event-desc">
                        Cuando Dra.Vetia genere el plan, aquí aparecerán las desparasitaciones internas y externas.
                      </p>
                    </article>
                  )
                ) : (
                  categoryList.map((ev) => {
                    const petName = petsMap[String(ev.idPet)] || "tu mascota";
                    const done = isDoneStatus(ev.status);
                    const label =
                      ev.eventType === "DEWORMING_INTERNAL"
                        ? `Desparasitación interna de ${petName}`
                        : `Desparasitación externa de ${petName}`;

                    return (
                      <div
                        key={ev.id}
                        role="button"
                        tabIndex={0}
                        className={`pf-cal-event-card pf-cal-event-card--deworming pf-cal-event-card--clickable ${
                          done ? "is-done" : ""
                        }`}
                        onClick={() => jumpToEvent(ev)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") jumpToEvent(ev);
                        }}
                      >
                        <div className="pf-cal-event-pill pf-cal-event-pill--deworming">
                          {label}
                        </div>

                        <h5 className="pf-cal-event-title">{ev.title}</h5>
                        <p className="pf-cal-event-date">{formatListDate(ev)}</p>

                        {ev.description && (
                          <p className="pf-cal-event-desc">
                            {normalizeDescription(ev.description)}
                          </p>
                        )}

                        {ev.status && (
  <p className="pf-cal-event-status">
    Estado: <strong>{normStatus(ev.status)}</strong>
  </p>
)}

{!done && (
  <button
    type="button"
    className="pf-cal-edit-date-btn"
    onClick={(e) => {
      e.stopPropagation();
      editVaccineDate(ev);
    }}
  >
    <Pencil size={14} />
    Cambiar fecha
  </button>
)}

{!done && (
  <button
    type="button"
    className="pf-cal-done-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await markDewormingDone(ev);
                            }}
                          >
                            Marcar como hecha
                          </button>
                        )}

                        {done && (
                          <button
                            type="button"
                            className="pf-cal-done-btn is-done"
                            disabled
                          >
                            Hecho
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            ) : (
              <>
                {categoryList.length === 0 ? (
                  <article className="pf-cal-event-card">
                    <h5 className="pf-cal-event-title">
                      {openCategory === "birthday"
                        ? "No tienes cumpleaños registrados"
                        : "No hay fechas especiales"}
                    </h5>

                    <p className="pf-cal-event-desc">
                      {openCategory === "birthday"
                        ? "Agrega mascotas con fecha de nacimiento para verlos aquí."
                        : "Vuelve más tarde para ver fechas especiales."}
                    </p>
                  </article>
                ) : (
                  categoryList.map((ev) => (
                    <div
                      key={ev.id}
                      role="button"
                      tabIndex={0}
                      className={`pf-cal-event-card pf-cal-event-card--${ev.type} pf-cal-event-card--clickable`}
                      onClick={() => jumpToEvent(ev)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") jumpToEvent(ev);
                      }}
                    >
                      <div className="pf-cal-event-pill">
                        {ev.type === "birthday" ? "Cumpleaños" : "Fecha especial"}
                      </div>

                      <h5 className="pf-cal-event-title">{ev.title}</h5>
                      <p className="pf-cal-event-date">{formatListDate(ev)}</p>

                      {ev.description && (
                        <p className="pf-cal-event-desc">{ev.description}</p>
                      )}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}

        {!openCategory && selectedEvents.length > 0 && (
          <div className="pf-cal-events-list">
            {selectedEvents.map((ev) => {
              const petName = petsMap[String(ev.idPet)] || "tu mascota";
              const showConsult = shouldShowConsultBtn(ev);
              const done =
                ev.type === "vaccine" || ev.type === "deworming"
                  ? isDoneStatus(ev.status)
                  : false;

              return (
                <article
                  key={ev.id}
                  className={`pf-cal-event-card pf-cal-event-card--${ev.type} ${
                    (ev.type === "vaccine" || ev.type === "deworming") && done ? "is-done" : ""
                  }`}
                >
                  <div
                    className={
                      ev.type === "deworming"
                        ? "pf-cal-event-pill pf-cal-event-pill--deworming"
                        : "pf-cal-event-pill"
                    }
                  >
                    {ev.type === "vaccine"
                      ? `Vacuna de ${petName}`
                      : ev.type === "deworming"
                      ? String(ev.eventType || "").toUpperCase() === "DEWORMING_INTERNAL"
                        ? `Desparasitación interna de ${petName}`
                        : `Desparasitación externa de ${petName}`
                      : ev.type === "birthday"
                      ? "Cumpleaños"
                      : "Fecha especial"}
                  </div>

                  <h5 className="pf-cal-event-title">{ev.title}</h5>

                  <p className="pf-cal-event-date">
                    {selectedDate &&
                      selectedDate.toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                  </p>

                  {ev.description && (
                    <p className="pf-cal-event-desc">{normalizeDescription(ev.description)}</p>
                  )}

                  {(ev.type === "vaccine" || ev.type === "deworming") && ev.status && (
                    <p className="pf-cal-event-status">
                      Estado: <strong>{normStatus(ev.status)}</strong>
                    </p>
                  )}

                  {ev.type === "deworming" && !done && (
  <button
    type="button"
    className="pf-cal-edit-date-btn"
    onClick={(e) => {
      e.stopPropagation();
      editVaccineDate(ev);
    }}
  >
    <Pencil size={14} />
    Cambiar fecha
  </button>
)}

                  {ev.type === "vaccine" && !done && (
  <button
    type="button"
    className="pf-cal-edit-date-btn"
    onClick={(e) => {
      e.stopPropagation();
      editVaccineDate(ev);
    }}
  >
    <Pencil size={14} />
    Cambiar fecha
  </button>
)}

                  {ev.type === "vaccine" && !done && (
                    <button
                      type="button"
                      className="pf-cal-done-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await markVaccineDone(ev);
                      }}
                    >
                      Marcar como aplicada
                    </button>
                  )}

                  {ev.type === "vaccine" && done && (
                    <button type="button" className="pf-cal-done-btn is-done" disabled>
                      Hecho
                    </button>
                  )}

                  {ev.type === "deworming" && !done && (
                    <button
                      type="button"
                      className="pf-cal-done-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await markDewormingDone(ev);
                      }}
                    >
                      Marcar como hecha
                    </button>
                  )}

                  {ev.type === "deworming" && done && (
                    <button type="button" className="pf-cal-done-btn is-done" disabled>
                      Hecho
                    </button>
                  )}

                  {ev.type === "vaccine" && showConsult && (
                    <button
                      type="button"
                      className="pf-cal-consult-btn"
                      onClick={() => handleScheduleConsult(ev)}
                    >
                      Programar consulta
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}